"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Form,
  type FormFieldSchema,
  Markdown,
  Message,
  Modal,
  ProgressBar,
  RadioButtons,
  Slides,
  type SlidesRef,
  Spacing,
  Text,
  useCountdown,
  useMaybeNavigationActionInterceptor,
  usePubSub,
} from "@huub-web/ui";

import {
  useAgentFormAwareness,
  useInputUpdate,
  useTemplates,
  type WizardStepInfo,
} from "../../hooks";
import type { AssessmentTestTemplateProps } from "./types";

/**
 * Props for the inner form — extends the public template props with
 * an internal callback that bumps the outer Form's key to force a remount.
 */
type InnerFormProps = AssessmentTestTemplateProps & {
  /**
   * Called after the inner reset completes so the parent can bump the
   * Form's key, discarding Form's internal dirty-tracking state.
   */
  onResetRequested: () => void;
};

/**
 * Renders the body of the K&E-toets as a horizontal slide deck: an
 * `intro` slide, an optional `membership` pre-question slide (when the
 * backend payload includes one), then one slide per numbered question.
 * Slide transitions are animated by the `<Slides>` component — matching
 * the MaxMortgageWizard UX. Lives inside the `<Form>` so later tasks can
 * hook into its FormContext.
 */
const InnerForm: FC<InnerFormProps> = ({
  id,
  intro,
  membership,
  questions,
  timeLimitSeconds,
  warnAtSeconds,
  version,
  submitEndpoint,
  onResetRequested,
}) => {
  const { t } = useTranslation();
  const { $post } = useTemplates();
  const pubSub = usePubSub();
  const interceptor = useMaybeNavigationActionInterceptor();

  /**
   * Imperative handle to the `<Slides>` component — used to drive
   * next/previous transitions from the Back/Next buttons.
   */
  const slidesRef = useRef<SlidesRef>(null);

  /**
   * Stable ref to the current `handleNext` — used by the agent
   * `submitForm` handler to avoid a stale closure.
   */
  const submitRef = useRef<() => void>(undefined);

  /**
   * Key of the currently active slide. `"intro"` for the intro panel,
   * or a question id (e.g. `"q1"`) for a question panel.
   */
  const [currentSlideKey, setCurrentSlideKey] = useState<string>("intro");

  /**
   * Selected answers keyed by question id.
   */
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [acknowledgements, setAcknowledgements] = useState<
    Record<string, boolean>
  >(Object.fromEntries(intro.requiredCheckboxes.map((cb) => [cb.id, false])));

  /**
   * Tracks whether the countdown has expired. When true, the expiry modal
   * renders on top of the form, blocking further interaction.
   */
  const [expired, setExpired] = useState(false);

  /**
   * Guards the submit action against double-clicks and disables the form
   * while the submit POST is in flight. Stays `true` on success — the BE
   * dispatches a websocket navigate which unmounts us — and resets to
   * `false` only on error so the user can retry.
   */
  const [submitting, setSubmitting] = useState(false);

  const countdown = useCountdown({
    limitSeconds: timeLimitSeconds,
    warnAtSeconds,
    onExpire: () => setExpired(true),
  });

  /**
   * Toggles a single acknowledgement checkbox by id.
   */
  const toggle = (checkboxId: string) =>
    setAcknowledgements((prev) => ({
      ...prev,
      [checkboxId]: !prev[checkboxId],
    }));

  const allAcknowledged = intro.requiredCheckboxes.every(
    (cb) => acknowledgements[cb.id],
  );

  /**
   * True once the user has made any visible progress — at least one
   * acknowledgement checked or at least one answer selected. Gates the
   * navigation guard so the first-open empty state doesn't nag.
   */
  const hasProgress =
    Object.values(acknowledgements).some(Boolean) ||
    Object.keys(answers).length > 0;

  /**
   * Register a navigation interceptor while the user has progress and is
   * not mid-submit. Closing the card (or any navigate action) routes
   * through `NavigationStateProvider.requestNavigation`, which consults
   * registered interceptors — ours returns `true`, triggering the
   * "navigate away?" modal. On submit we unregister synchronously so the
   * post-submit websocket navigate proceeds without the modal.
   */
  useEffect(() => {
    if (!interceptor) return;
    if (!hasProgress) return;
    if (submitting) return;
    return interceptor.registerInterceptor(() => true);
  }, [interceptor, hasProgress, submitting]);

  const isIntro = currentSlideKey === "intro";
  const isMembership =
    membership !== undefined && currentSlideKey === membership.id;
  const currentQuestionIndex =
    isIntro || isMembership
      ? -1
      : questions.findIndex((q) => q.id === currentSlideKey);
  const currentQuestion =
    currentQuestionIndex >= 0 ? questions[currentQuestionIndex] : null;
  const isFinalStep = !isIntro && currentQuestionIndex === questions.length - 1;
  /**
   * 1-based progress number for the "Vraag X van N" label on question
   * steps. Returns 0 on intro/membership, where we either hide the
   * progress label (intro) or use `membership.title` from the backend
   * payload as the label (membership) instead.
   */
  const currentStepNumber = currentQuestionIndex + 1;

  /**
   * On numbered-question steps, `currentQuestion` is guaranteed to be
   * defined because the outer `AssessmentTestTemplate` throws when
   * `questions.length === 0`, and `currentSlideKey` only ever becomes a
   * question id via `<Slides>` (which maps from our slides dict). We use
   * optional chaining instead of a non-null assertion to comply with lint
   * rules. The membership step is gated by its own answer key.
   */
  const nextDisabled = isIntro
    ? !allAcknowledged
    : isMembership && membership
      ? !answers[membership.id]
      : !(currentQuestion && answers[currentQuestion.id]);

  /**
   * Synthetic field schema: `<AssessmentTestTemplate>` uses raw <Checkbox>
   * and <RadioButtons> with local state, so Form.getFieldSchema() returns
   * []. We build the schema by hand so the AI agent sees every
   * acknowledgement + question, keyed by step.
   */
  const fieldSchema: FormFieldSchema[] = [
    ...intro.requiredCheckboxes.map((cb) => ({
      name: cb.id,
      label: cb.label,
      type: "boolean" as const,
      required: true,
      value: acknowledgements[cb.id] ?? false,
      errors: [],
      step: "intro",
    })),
    ...(membership
      ? [
          {
            name: membership.id,
            label: membership.label,
            type: "radio" as const,
            required: true,
            options: membership.options,
            value: answers[membership.id] ?? null,
            errors: [],
            step: membership.id,
          },
        ]
      : []),
    ...questions.map((q) => ({
      name: q.id,
      label: q.label,
      type: "radio" as const,
      required: true,
      options: q.options,
      value: answers[q.id] ?? null,
      errors: [],
      step: q.id,
    })),
  ];

  /**
   * Wizard step metadata. One `intro` panel, optionally one `membership`
   * pre-question panel, then one panel per numbered question.
   */
  const steps: WizardStepInfo[] = [
    { key: "intro", title: t("assessmentTest.progress.intro") },
    ...(membership ? [{ key: membership.id, title: membership.title }] : []),
    ...questions.map((q, i) => ({
      key: q.id,
      title: t("assessmentTest.progress.question", {
        current: i + 1,
        total: questions.length,
      }),
    })),
  ];

  /**
   * Index of the active slide within the wizard `steps` array. Used by
   * the agent-awareness hook so the agent knows which step the user is
   * on (intro=0, optional membership=1, then questions). We compute it
   * by matching `currentSlideKey` against the steps list rather than
   * doing branch-specific arithmetic — keeps it correct whether
   * `membership` is present or not.
   */
  const currentStepIndex = Math.max(
    steps.findIndex((s) => s.key === currentSlideKey),
    0,
  );

  useAgentFormAwareness({
    formId: id,
    formType: "wizard",
    fieldSchema,
    wizard: {
      currentStep: currentSlideKey,
      currentStepIndex,
      steps,
      totalSteps: steps.length,
    },
  });

  const checkboxIds = useRef(
    new Set(intro.requiredCheckboxes.map((cb) => cb.id)),
  );
  const questionIds = useRef(
    new Set([
      ...(membership ? [membership.id] : []),
      ...questions.map((q) => q.id),
    ]),
  );

  /**
   * Refs that track slide position so the stable handleAgentUpdate
   * callback can guard against wrap-around without re-creating itself.
   */
  const isIntroRef = useRef(isIntro);
  isIntroRef.current = isIntro;
  const isFinalStepRef = useRef(isFinalStep);
  isFinalStepRef.current = isFinalStep;

  /**
   * Handle agent field updates: navigation controls, acknowledgement
   * checkboxes, and answer selections.
   */
  const handleAgentUpdate = useCallback((updates: Record<string, unknown>) => {
    if (updates.nextPanel) {
      if (isFinalStepRef.current) {
        submitRef.current?.();
      } else {
        slidesRef.current?.next();
      }
      return;
    }
    if (updates.previousPanel) {
      if (!isIntroRef.current) {
        slidesRef.current?.previous();
      }
      return;
    }
    if (updates.submitForm) {
      submitRef.current?.();
      return;
    }

    for (const [field, value] of Object.entries(updates)) {
      if (checkboxIds.current.has(field)) {
        setAcknowledgements((prev) => ({
          ...prev,
          [field]: value === true || value === "true",
        }));
      } else if (questionIds.current.has(field) && typeof value === "string") {
        setAnswers((prev) => ({ ...prev, [field]: value }));
      }
    }
  }, []);

  useInputUpdate({
    prefix: `${id}__`,
    debugLabel: "AssessmentTestTemplate",
    onUpdate: handleAgentUpdate,
  });

  /**
   * Advances to the next slide, or — on the final step — submits the
   * assessment via `$post`. Uses a direct POST (not `apiRequest` pubsub)
   * so we can own the success/error branches: on success we leave
   * `submitting=true` because the BE dispatches a websocket navigate
   * that unmounts us; on error we reset `submitting` and surface a
   * snackbar so the user can retry.
   */
  const handleNext = async () => {
    if (isIntro) {
      slidesRef.current?.next();
      return;
    }
    if (isFinalStep) {
      if (submitting) return;
      setSubmitting(true);
      try {
        await $post(submitEndpoint, {
          version,
          acknowledgements,
          answers,
        } as any);
      } catch (error) {
        console.error("[AssessmentTestTemplate] submit failed", error);
        pubSub.publish("snackbar", {
          body: t("general.error"),
          variant: "error",
        });
        setSubmitting(false);
      }
      return;
    }
    slidesRef.current?.next();
  };

  submitRef.current = handleNext;

  /**
   * Returns to the previous slide. `<Slides>` handles direction + animation;
   * `handleSlideChange` below syncs `currentSlideKey` so our derived state
   * stays in sync.
   */
  const handleBack = () => {
    slidesRef.current?.previous();
  };

  /**
   * Resets all internal state back to the intro step and asks the parent
   * to remount <Form>. The remount reinitializes the useCountdown hook
   * and remounts <Slides> with `initialSlide="intro"` — clearing the
   * imperative nav history without needing to call `goTo("intro")`.
   */
  const handleReset = () => {
    setAcknowledgements(
      Object.fromEntries(intro.requiredCheckboxes.map((cb) => [cb.id, false])),
    );
    setAnswers({});
    setCurrentSlideKey("intro");
    setExpired(false);
    onResetRequested();
  };

  /**
   * Called by `<Slides>` whenever the active slide changes (via
   * `next()`, `previous()`, or `goTo()`). Keeps our derived state
   * (`isIntro`, `currentQuestion`, progress label, agent awareness)
   * in sync with the slide deck.
   */
  const handleSlideChange = (key: string) => {
    setCurrentSlideKey(key);
  };

  /**
   * Slide definitions — one intro panel + one panel per question.
   * `<Slides>` renders the active slide and animates transitions;
   * we drive navigation imperatively via `slidesRef`.
   */
  const slides: Record<string, ReactNode> = {
    intro: (
      <Flex direction="column" gap="m">
        <Markdown content={intro.markdown} />
        <Card variant="tertiary">
          {intro.requiredCheckboxes.map((cb) => (
            <Checkbox
              key={cb.id}
              id={cb.id}
              checked={acknowledgements[cb.id] ?? false}
              label={cb.label}
              disabled={submitting}
              onChange={() => toggle(cb.id)}
            />
          ))}
        </Card>
      </Flex>
    ),
    ...(membership
      ? {
          [membership.id]: (
            <Flex key={membership.id} direction="column" gap="m">
              <Text variant="bodyM">{membership.label}</Text>
              <RadioButtons
                name={membership.id}
                value={answers[membership.id] ?? ""}
                disabled={submitting}
                onChange={(value) =>
                  setAnswers((prev) => ({ ...prev, [membership.id]: value }))
                }
                options={membership.options}
              />
              {membership.footer ? (
                <Text variant="bodyS" markdown>
                  {membership.footer}
                </Text>
              ) : null}
            </Flex>
          ),
        }
      : {}),
    ...Object.fromEntries(
      questions.map((q) => [
        q.id,
        <Flex key={q.id} direction="column" gap="m">
          <Text variant="bodyM" markdown>
            {q.label}
          </Text>
          <RadioButtons
            name={q.id}
            value={answers[q.id] ?? ""}
            disabled={submitting}
            onChange={(value) =>
              setAnswers((prev) => ({ ...prev, [q.id]: value }))
            }
            options={q.options}
          />
        </Flex>,
      ]),
    ),
  };

  return (
    <>
      <Flex direction="column" gap="m">
        {!isIntro && (
          <Flex gap="s" align="center" justify="space-between">
            <Text variant="bodyM" bold>
              {isMembership && membership
                ? membership.title
                : t("assessmentTest.progress.question", {
                    current: currentStepNumber,
                    total: questions.length,
                  })}
            </Text>
            <Flex gap="s" align="center">
              <ProgressBar
                progress={(countdown.remainingSeconds / timeLimitSeconds) * 100}
                aria-label={t("assessmentTest.timer.remaining")}
                style={{ width: 60 }}
              />
              <Text variant="monospace">{countdown.formatted}</Text>
            </Flex>
          </Flex>
        )}
        {!isIntro && countdown.shouldWarn && (
          <Message variant="warning">
            {t("assessmentTest.timer.warning", {
              count: Math.ceil(countdown.remainingSeconds / 60),
            })}
          </Message>
        )}
        <Slides
          ref={slidesRef}
          slides={slides}
          initialSlide="intro"
          onSlideChange={handleSlideChange}
        />
        <Spacing />
        <Flex justify="space-between">
          {!isIntro ? (
            <Button
              variant="tertiary"
              disabled={submitting}
              onClick={handleBack}
            >
              {t("assessmentTest.buttons.back")}
            </Button>
          ) : (
            <Spacing />
          )}
          <Button
            variant="primary"
            disabled={nextDisabled || submitting}
            onClick={handleNext}
          >
            {isFinalStep
              ? t("assessmentTest.buttons.submit")
              : t("assessmentTest.buttons.next")}
          </Button>
        </Flex>
      </Flex>
      {expired && (
        <Modal
          placement="center"
          size="small"
          title={t("assessmentTest.expired.title")}
        >
          <Flex direction="column" gap="m">
            <Text>{t("assessmentTest.expired.description")}</Text>
            <Flex justify="flex-end">
              <Button variant="primary" onClick={handleReset}>
                {t("assessmentTest.expired.cta")}
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};

/**
 * Renders the full K&E-toets flow: intro gate, 12 radio questions,
 * progress indicator, countdown timer, and submit.
 *
 * Form's built-in `trackDirty` only tracks `FormInput`-registered fields.
 * The template uses raw `<Checkbox>` + `<RadioButtons>` with local state,
 * so `InnerForm` registers its own navigation interceptor via
 * `useMaybeNavigationActionInterceptor` — that's what triggers the
 * navigate-away modal on close/back.
 *
 * On expiry-reset, `formKey` is bumped so `<Form>` remounts, which also
 * remounts `<Slides>` back to `initialSlide="intro"`.
 *
 * See docs/superpowers/specs/2026-04-21-assessment-test-card-design.md
 */
export const AssessmentTestTemplate: FC<AssessmentTestTemplateProps> = (
  props,
) => {
  if (props.questions.length === 0) {
    throw new Error("AssessmentTestTemplate: questions must not be empty");
  }

  const [formKey, setFormKey] = useState(0);
  const remountForm = () => setFormKey((k) => k + 1);

  return (
    <Form key={formKey} id={props.id} gap="m">
      <InnerForm {...props} onResetRequested={remountForm} />
    </Form>
  );
};
